import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, FormControl } from '@angular/forms';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { AlertService } from '../../../shared/alert/alert.service';
import { first } from 'rxjs/operators';
import * as _moment from 'moment';
const moment = _moment;
import { UserService } from '../../../core/user/user.service';
import { User } from '../../../models/user';
import { UtilsService } from '../../../services/utils.service';

@Component({
    selector: 'app-my-profile',
    templateUrl: './my-profile.component.html',
    styleUrls: ['./my-profile.component.scss']
})
export class MyProfileComponent implements OnInit {
    // A date of birth can't be in the future (T41074)
    readonly today = new Date();
    public form: FormGroup;
    loading = false;
    alertOptions = {
        autoClose: true,
        keepAfterRouteChange: false,
        id: 'profile-error'
    };
    user: User;
    editPassword = false;

    public countries = [];

    public cities = [];

    public constructor(
        private formBuilder: FormBuilder,
        private alertService: AlertService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {}

    ngOnInit(): void {
        this.getCountries();
        this.getUser();
        this.initForm();
    }

    getUser() {
        this.userService
            .getUser()
            .pipe(first())
            .subscribe({
                next: resp => {
                    this.user = resp;
                    this.initForm(this.user);
                },
                error: error => {
                    this.alertService.error('Error: ' + error, this.alertOptions);
                    this.initForm();
                }
            });
    }

    getCountries() {
        this.utilsService.getCountries().subscribe(resp => {
            this.countries = resp;
        });
    }

    private initForm(user?): void {
        this.form = this.formBuilder.group({
            gender: new FormControl(
                user ? user.gender : null,
                RxwebValidators.required({ message: 'Gender is required.' })
            ),
            firstName: new FormControl(
                user ? user.firstName : null,
                RxwebValidators.required({ message: 'First name is required.' })
            ),
            lastName: new FormControl(
                user ? user.lastName : null,
                RxwebValidators.required({ message: 'Last name is required.' })
            ),
            email: new FormControl(
                user ? user.email : null,
                RxwebValidators.required({ message: 'Email is required.' })
            ),
            dob: new FormControl(
                user ? user.dob : null,
                RxwebValidators.required({ message: 'Date of birth is required.' })
            ),
            country: new FormControl(
                user ? user.country : null,
                RxwebValidators.required({
                    message: 'Country is required.'
                })
            ),
            city: new FormControl(
                user ? user.city : null,
                RxwebValidators.required({
                    message: 'City is required.'
                })
            ),
            street: new FormControl(
                user ? user.street : null,
                RxwebValidators.required({
                    message: 'Street is required.'
                })
            ),
            building: new FormControl(
                user ? user.building : null,
                RxwebValidators.required({
                    message: 'Building/Floor is required.'
                })
            )
        });
    }

    onChangePassword() {
        this.editPassword = !this.editPassword;
        if (this.editPassword) {
            this.form.addControl(
                'password',
                new FormControl(
                    null,
                    RxwebValidators.required({ message: 'Password is required.' })
                )
            );
            this.form.addControl(
                'password_confirmation',
                new FormControl(null, [
                    RxwebValidators.required({
                        message: 'Password confirmation is required.'
                    }),
                    RxwebValidators.compare({
                        fieldName: 'password',
                        message: 'Password confirmation should match password'
                    })
                ])
            );
        } else {
            this.form.removeControl('password');
            this.form.removeControl('password_confirmation');
        }
    }

    get f() {
        return this.form.controls;
    }

    saveProfile() {
        this.alertService.clear();
        // stop here if form is invalid
        if (this.form.invalid) {
            return;
        }
        this.loading = true;
        // Send the calendar date itself: a picked date serialises as a UTC timestamp, which in
        // UTC+ zones is the day before, and the API keeps only the date part.
        const value = { ...this.form.value };
        if (value.dob) {
            value.dob = moment(value.dob).format('YYYY-MM-DD');
        }
        this.userService
            .updateUser(value)
            .pipe(first())
            .subscribe({
                next: () => {
                    this.alertService.success('Profile updated successfully', this.alertOptions);
                },
                error: error => {
                    this.alertService.error('Error: ' + error, this.alertOptions);
                    this.loading = false;
                }
            });
    }
}
